import type { GetSessionDetailOutput } from "@/lib/ai/tools/get-session-detail/server";
import {
  buildDataset,
  buildFields,
  createTableOnlyProfile,
  isRecord,
  toNumber,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type { VizDataset } from "@/lib/ai/visualization/data/schema";

const TABLE_ONLY_PROFILE = createTableOnlyProfile({
  datasetFamily: "ua.sessionDetail",
});

function toParamsPreview(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) return null;

  const pairs: string[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    const key = toString(item.key);
    const raw = item.value;
    const str =
      toString(raw) ??
      (typeof raw === "boolean" ? (raw ? "true" : "false") : null) ??
      (toNumber(raw) !== null ? String(toNumber(raw)) : null);
    if (!key || !str) continue;
    pairs.push(`${key}=${str}`);
  }
  if (pairs.length === 0) return null;
  return pairs.join(", ");
}

function buildSessionSummaryDataset(
  output: Record<string, unknown>,
  source: VizDataset["source"],
): VizDataset {
  const geography = isRecord(output.geography) ? output.geography : null;
  const device = isRecord(output.device) ? output.device : null;
  const events = Array.isArray(output.events) ? output.events : [];
  const eventCount = events.filter((event) => isRecord(event)).length;
  const sessionIdNumber = toNumber(output.gaSessionId);

  return buildDataset({
    id: "ua-session-detail-summary",
    profile: TABLE_ONLY_PROFILE,
    fields: buildFields([
      { key: "gaSessionId", role: "dim", type: "string" },
      { key: "userPseudoId", role: "dim", type: "string" },
      { key: "userFirstTouchTimestamp", role: "dim", type: "string" },
      { key: "sessionStartTime", role: "dim", type: "string" },
      { key: "sessionEndTime", role: "dim", type: "string" },
      {
        key: "sessionDuration",
        role: "metric",
        type: "number",
        unit: "duration",
      },
      { key: "source", role: "dim", type: "string" },
      { key: "medium", role: "dim", type: "string" },
      { key: "campaign", role: "dim", type: "string" },
      { key: "term", role: "dim", type: "string" },
      { key: "content", role: "dim", type: "string" },
      { key: "firstVisitSource", role: "dim", type: "string" },
      { key: "firstVisitMedium", role: "dim", type: "string" },
      { key: "firstVisitCampaign", role: "dim", type: "string" },
      { key: "pageReferrer", role: "dim", type: "string" },
      { key: "country", role: "dim", type: "string" },
      { key: "region", role: "dim", type: "string" },
      { key: "city", role: "dim", type: "string" },
      { key: "deviceCategory", role: "dim", type: "string" },
      { key: "deviceOs", role: "dim", type: "string" },
      { key: "deviceOsVersion", role: "dim", type: "string" },
      { key: "deviceModel", role: "dim", type: "string" },
      { key: "deviceBrowser", role: "dim", type: "string" },
      { key: "deviceBrowserVersion", role: "dim", type: "string" },
      { key: "language", role: "dim", type: "string" },
      { key: "eventCount", role: "metric", type: "number", unit: "count" },
      { key: "sessionExplorerUrl", role: "dim", type: "string" },
    ]),
    rows: [
      {
        gaSessionId:
          toString(output.gaSessionId) ??
          (sessionIdNumber !== null ? String(sessionIdNumber) : null),
        userPseudoId: toString(output.userPseudoId),
        userFirstTouchTimestamp: toString(output.userFirstTouchTimestamp),
        sessionStartTime: toString(output.sessionStartTime),
        sessionEndTime: toString(output.sessionEndTime),
        sessionDuration: toNumber(output.sessionDuration),
        source: toString(output.source),
        medium: toString(output.medium),
        campaign: toString(output.campaign),
        term: toString(output.term),
        content: toString(output.content),
        firstVisitSource: toString(output.firstVisitSource),
        firstVisitMedium: toString(output.firstVisitMedium),
        firstVisitCampaign: toString(output.firstVisitCampaign),
        pageReferrer: toString(output.pageReferrer),
        country: toString(geography?.country),
        region: toString(geography?.region),
        city: toString(geography?.city),
        deviceCategory: toString(device?.category),
        deviceOs: toString(device?.os),
        deviceOsVersion: toString(device?.osVer),
        deviceModel: toString(device?.model),
        deviceBrowser: toString(device?.browser),
        deviceBrowserVersion: toString(device?.browserVer),
        language: toString(device?.language),
        eventCount,
        sessionExplorerUrl:
          toString(output.sessionExplorerUrl) ??
          toString(output.sessionExplorerPath),
      },
    ],
    source,
    defaults: {
      category: "gaSessionId",
      value: "eventCount",
    },
    notes:
      "Table-only session summary. This payload is not chart-first due to single-session granularity.",
  });
}

function buildSessionEventsDataset(
  output: Record<string, unknown>,
  source: VizDataset["source"],
): VizDataset | null {
  const events = Array.isArray(output.events) ? output.events : [];
  const rows = events
    .filter((event) => isRecord(event))
    .map((event, index) => ({
      eventOrder: index + 1,
      eventName: toString(event.eventName),
      eventTimestamp: toString(event.eventTimestamp),
      pageTitle: toString(event.pageTitle),
      pageLocation: toString(event.pageLocation),
      pageReferrer: toString(event.pageReferrer),
      contentGroup: toString(event.contentGroup),
      eventParamsPreview: toParamsPreview(event.eventParams),
    }));

  if (rows.length === 0) return null;

  return buildDataset({
    id: "ua-session-detail-events",
    profile: TABLE_ONLY_PROFILE,
    fields: buildFields([
      { key: "eventOrder", role: "metric", type: "number", unit: "count" },
      { key: "eventName", role: "dim", type: "string" },
      { key: "eventTimestamp", role: "dim", type: "string" },
      { key: "pageTitle", role: "dim", type: "string" },
      { key: "pageLocation", role: "dim", type: "string" },
      { key: "pageReferrer", role: "dim", type: "string" },
      { key: "contentGroup", role: "dim", type: "string" },
      { key: "eventParamsPreview", role: "dim", type: "string" },
    ]),
    rows,
    source,
    defaults: {
      category: "eventName",
      value: "eventOrder",
    },
    notes:
      "Table-only event timeline for a single session. Bar/line charts are intentionally disabled.",
  });
}

/**
 * 単一セッション詳細出力を summary/events の table dataset 群へ変換する。
 */
export function buildUaSessionDetailDatasets(
  rawOutput: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  // adapter 契約外の payload（null/非 object）はここで停止して誤推論を防ぐ。
  if (typeof rawOutput !== "object" || rawOutput === null) return [];
  const output = rawOutput as Exclude<GetSessionDetailOutput, null> &
    Record<string, unknown>;

  const datasets: VizDataset[] = [buildSessionSummaryDataset(output, source)];
  const eventsDataset = buildSessionEventsDataset(output, source);
  if (eventsDataset) datasets.push(eventsDataset);
  return datasets;
}
